import React, { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextInput, Textarea } from 'ui-library';
import { FILE_TYPES_ALLOWED } from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import Error from 'components/AtomicStructure/atoms/Error';
import UploadDocuments from 'components/UploadDocuments/UploadDocuments';
import { TranslationFieldsProps } from './SystemMessage.types';

export const TranslationFieldsComponent = ({
    lang,
    activeLang,
    control,
    setError,
    setValue,
}: TranslationFieldsProps) => {
    const { t } = useTranslation();

    const hidden = lang !== activeLang;

    const handleChange = useCallback(({ value }) => {
        setError?.(`translations.${activeLang}.document`, {
            message: undefined,
        });
        setValue?.(`translations.${activeLang}.document`, value || '');
    }, [activeLang]);

    const handleError = useCallback(({ error }) => {
        setError?.(`translations.${activeLang}.document`, { message: error });
    }, [activeLang]);

    return (
        <div style={{ display: hidden ? 'none' : 'block' }}>
            {/* TITLE */}
            <Controller
                control={control}
                name={`translations.${lang}.title`}
                render={({ field, fieldState }) => (
                    <div className="mb-16">
                        <TextInput
                            {...field}
                            data-cy={`translations.${lang}.title`}
                            label={t('SystemMessage.Form.FieldLabel.Title')}
                            placeholder={t(
                                'SystemMessage.Form.Placeholder.Title',
                            )}
                            error={fieldState?.error?.message || ''}
                            required
                            maxlength={140}
                        />
                        <div className="system-message-form--character-count">
                            {field.value?.length || 0}
                            /140
                        </div>
                    </div>
                )}
            />

            {/* BODY */}
            <Controller
                control={control}
                name={`translations.${lang}.body`}
                render={({ field, fieldState }) => (
                    <div className="mb-16">
                        <Textarea
                            {...field}
                            label={`${t('SystemMessage.Form.FieldLabel.Body')} *`}
                            placeholder={t(
                                'SystemMessage.Form.Placeholder.Body',
                            )}
                            id={`translations.${lang}.body`}
                            row={5}
                            error={fieldState?.error?.message}
                            maxlength={1000}
                        />
                        <div className="system-message-form--character-count">
                            {field.value?.length || 0}
                            /1000
                        </div>
                    </div>
                )}
            />

            {/* LINK */}
            <Controller
                control={control}
                name={`translations.${lang}.link`}
                render={({ field, fieldState }) => (
                    <div className="mb-40">
                        <TextInput
                            {...field}
                            label={t('SystemMessage.Form.FieldLabel.Link')}
                            placeholder={t(
                                'SystemMessage.Form.Placeholder.Link',
                            )}
                            error={fieldState?.error?.message || ''}
                        />
                    </div>
                )}
            />

            {/* DOCUMENT */}
            <Controller
                name={`translations.${lang}.document`}
                control={control}
                render={({ fieldState, field: { ref, value } }) => (
                    <div className="upload-file-container mb-16">
                        <h3>
                            {t(
                                'SystemMessage.Form.FieldLabel.UploadDocument',
                            )}
                        </h3>
                        <div className="mb-16">
                            {t(
                                'SystemMessage.Form.FieldLabel.AllowedFile',
                                { count: 1 },
                            )}
                            {' '}
                            (JPG, JPEG, PNG, PDF)
                        </div>
                        <UploadDocuments
                            fileTypesAllowed={FILE_TYPES_ALLOWED}
                            ref={ref}
                            onSuccess={(val) => handleChange({ value: val[0] })}
                            onError={(err) => handleError({ error: err })}
                            className={`mt-20 ${fieldState?.error?.message ? 'error' : ''}`}
                            numberOfFiles={1}
                            maxMbFileSize={30}
                            defaultFiles={value ? [value] : []}
                        />
                        {fieldState?.error?.message && (
                            <Error>
                                {fieldState?.error?.message?.toString()}
                            </Error>
                        )}
                    </div>
                )}
            />
        </div>
    );
};
